// MobilePlayPreview — explains the play flow with:
//   • 3 stylised CSS phone mockups (Join → Tap to mark → BINGO!) — always crisp.
//   • 3 REAL gameplay screenshots: player view, share modal, host dashboard
//     (served from /screenshots/{player-view,share-modal,host-view}.png).
// Used on the Music Bingo page AND on the Landing page (replacing one of
// the theme banners) per customer feedback that the digital play flow was
// unclear and the Pro options/USPs need to be visible without scrolling.
import { useState, useEffect } from "react";
import { Smartphone, QrCode, MousePointerClick, PartyPopper, ArrowRight, Eye, X as XIcon } from "lucide-react";
import { useI18n } from "../lib/i18n";

function PhoneFrame({ children, label, badge, badgeColor = "var(--brand-yellow)" }) {
  return (
    <div className="flex flex-col items-center">
      {/* Step badge above phone */}
      <div
        className="nb-border rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-3"
        style={{ background: badgeColor, color: "#000" }}
      >
        {badge}
      </div>
      {/* Phone shell */}
      <div
        className="relative bg-black rounded-[28px] p-[6px] nb-shadow-lg"
        style={{ width: 200, boxShadow: "5px 5px 0 #000" }}
      >
        {/* Notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-3 bg-black rounded-b-xl z-10"></div>
        {/* Screen */}
        <div
          className="bg-white rounded-[22px] overflow-hidden"
          style={{ width: 188, height: 380 }}
        >
          {children}
        </div>
      </div>
      <div className="text-xs font-bold text-neutral-700 mt-3 text-center max-w-[200px] leading-tight">
        {label}
      </div>
    </div>
  );
}

// ─── Phone #1: Join screen with QR + code ─────────────────────────────────
function PhoneJoinScreen({ joinLabel, codeLabel, scanLabel }) {
  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-[#FFF8E1] to-white">
      <div className="bg-[var(--brand-red)] text-white px-3 py-2 pt-5 text-center">
        <div className="font-display font-black text-[10px] tracking-tight">bingocards-generator</div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-3 text-center">
        <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-500 mb-1">
          {joinLabel}
        </div>
        <div className="font-display font-black text-base mb-3">{scanLabel}</div>
        {/* Stylised QR code (CSS grid pattern) */}
        <div className="bg-white nb-border rounded p-1.5 mb-3">
          <div className="grid grid-cols-9 grid-rows-9 gap-px w-[88px] h-[88px]">
            {Array.from({ length: 81 }).map((_, i) => {
              // Pseudo-random but deterministic QR pattern
              const on = [0,1,2,3,4,8,9,13,17,18,22,26,27,28,29,30,31,32,33,34,35,36,40,44,45,49,53,54,58,62,63,67,71,72,73,74,75,76,77,78,79,80,5,6,7,16,25,42,43,46,47,48,60,61,65,66,69,70].includes(i);
              return <div key={i} className={on ? "bg-black" : "bg-white"} />;
            })}
            {/* Corner markers */}
            <div className="absolute top-1.5 left-1.5 w-4 h-4 border-2 border-black bg-white"></div>
            <div className="absolute top-1.5 right-1.5 w-4 h-4 border-2 border-black bg-white"></div>
            <div className="absolute bottom-1.5 left-1.5 w-4 h-4 border-2 border-black bg-white"></div>
          </div>
        </div>
        <div className="text-[10px] font-bold text-neutral-500 mb-1">— {codeLabel} —</div>
        <div className="font-mono font-black text-xl tracking-[0.2em] nb-border bg-[var(--brand-yellow)] px-2.5 py-1 rounded">
          K7A3
        </div>
      </div>
    </div>
  );
}

// ─── Phone #2: Bingo card with some cells marked ─────────────────────────
function PhoneCardScreen({ titleLabel, tapLabel, themeColor = "#6B2BD9" }) {
  const cells = [
    "Queen","ABBA","Beatles","Madonna","Prince",
    "Wham","U2","Dire Straits","Kim Wilde","Toto",
    "Cyndi Lauper","FREE","Tina Turner","Bon Jovi","Bowie",
    "Hall & Oates","Police","Phil Collins","Genesis","Sting",
    "INXS","Talking Heads","Cure","Duran","Eurythmics",
  ];
  // Marked indices (free + winning row diagonal-ish)
  const marked = new Set([0, 6, 11, 12, 18, 24, 14]);
  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <div className="px-3 py-2 pt-5 text-center" style={{ background: themeColor, color: "#fff" }}>
        <div className="font-display font-black text-[11px] tracking-tight">{titleLabel}</div>
        <div className="text-[8px] opacity-90 mt-0.5">#3 — Anna</div>
      </div>
      <div className="px-2 py-2">
        {/* BINGO row */}
        <div className="grid grid-cols-5 gap-1 mb-1">
          {"BINGO".split("").map((l) => (
            <div
              key={l}
              className="font-display font-black text-[9px] flex items-center justify-center rounded nb-border py-0.5"
              style={{ background: themeColor, color: "#fff" }}
            >{l}</div>
          ))}
        </div>
        {/* Cells */}
        <div className="grid grid-cols-5 gap-1">
          {cells.map((c, i) => {
            const isFree = c === "FREE";
            const isMarked = marked.has(i);
            return (
              <div
                key={i}
                className="aspect-square flex items-center justify-center text-center nb-border rounded relative overflow-hidden"
                style={{
                  background: isMarked ? "#0FB36A" : (isFree ? themeColor : "#fff"),
                  color: isMarked || isFree ? "#fff" : "#111",
                  fontSize: c.length > 8 ? "4px" : "5px",
                  fontWeight: 800,
                  padding: "1px",
                  lineHeight: 1,
                }}
              >
                {c}
                {isMarked && !isFree && (
                  <span className="absolute inset-0 flex items-center justify-center text-white text-[14px] font-black">✓</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-auto px-3 py-2 bg-[var(--brand-yellow)] nb-border border-x-0 border-b-0 text-center">
        <div className="text-[8px] font-bold uppercase tracking-widest mb-0.5">{tapLabel}</div>
        <div className="inline-flex items-center gap-1 text-[9px] font-bold text-black">
          <MousePointerClick size={10} /> Tap = mark
        </div>
      </div>
    </div>
  );
}

// ─── Phone #3: BINGO! celebration ─────────────────────────────────────────
function PhoneWinScreen({ winLabel, sharedLabel }) {
  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-[#FFCC00] via-[#FF3B30] to-[#6B2BD9] relative overflow-hidden">
      {/* Confetti dots */}
      {Array.from({ length: 20 }).map((_, i) => (
        <span
          key={i}
          className="absolute w-1.5 h-1.5 rounded-sm"
          style={{
            top: `${(i * 37) % 90}%`,
            left: `${(i * 53) % 95}%`,
            background: ["#fff","#FFCC00","#0FB36A","#1E88E5"][i % 4],
            transform: `rotate(${i * 23}deg)`,
            opacity: 0.85,
          }}
        />
      ))}
      <div className="bg-black/30 backdrop-blur px-3 py-2 pt-5 text-center">
        <div className="font-display font-black text-[10px] tracking-tight text-white">bingocards-generator</div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-center px-3 relative z-10">
        <div className="font-display font-black text-white text-3xl leading-none mb-1 drop-shadow-lg">
          BINGO!
        </div>
        <div className="text-[9px] font-black uppercase tracking-widest text-white/95 mb-3">
          {winLabel}
        </div>
        <div className="bg-white nb-border rounded-md px-3 py-2 nb-shadow-sm">
          <PartyPopper size={20} className="text-[var(--brand-red)] mx-auto mb-1" />
          <div className="text-[9px] font-bold leading-tight">{sharedLabel}</div>
        </div>
      </div>
    </div>
  );
}

export default function MobilePlayPreview() {
  const { t } = useI18n();
  const [openScreens, setOpenScreens] = useState(false);
  return (
    <section className="nb-card-lg rounded-lg p-6 sm:p-10 bg-white" data-testid="mobile-play-preview">
      <div className="inline-flex items-center gap-2 nb-border bg-[var(--brand-blue,#1E88E5)] text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest nb-shadow-sm mb-4">
        <Smartphone size={14} /> {t("mp_badge")}
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-3">
        {t("mp_title")}
      </h2>
      <p className="text-base text-neutral-700 mb-8 max-w-3xl font-medium leading-relaxed">
        {t("mp_intro")}
      </p>

      {/* Three-phone storyboard */}
      <div className="relative">
        <div className="grid sm:grid-cols-3 gap-6 sm:gap-4 lg:gap-8 items-start justify-items-center">
          <div className="flex flex-col items-center relative">
            <PhoneFrame badge={t("mp_step1_badge")} label={t("mp_step1_label")} badgeColor="var(--brand-yellow)">
              <PhoneJoinScreen
                joinLabel={t("mp_join_kicker")}
                scanLabel={t("mp_scan_or_code")}
                codeLabel={t("mp_or_code")}
              />
            </PhoneFrame>
            <ArrowRight className="hidden sm:block absolute -right-4 lg:-right-6 top-32 text-neutral-400" size={28} strokeWidth={3} />
          </div>
          <div className="flex flex-col items-center relative">
            <PhoneFrame badge={t("mp_step2_badge")} label={t("mp_step2_label")} badgeColor="#1DB954">
              <PhoneCardScreen
                titleLabel="MUZIEK BINGO"
                tapLabel={t("mp_tap_to_mark")}
              />
            </PhoneFrame>
            <ArrowRight className="hidden sm:block absolute -right-4 lg:-right-6 top-32 text-neutral-400" size={28} strokeWidth={3} />
          </div>
          <PhoneFrame badge={t("mp_step3_badge")} label={t("mp_step3_label")} badgeColor="var(--brand-red)">
            <PhoneWinScreen
              winLabel={t("mp_auto_detect")}
              sharedLabel={t("mp_wall_of_fame")}
            />
          </PhoneFrame>
        </div>
      </div>

      {/* Bullet recap below the phones */}
      <div className="mt-10 grid sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="nb-border rounded-md p-3 bg-[#FFF8E1] flex items-start gap-2">
          <QrCode size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-xs font-medium leading-snug">
            <div className="font-bold mb-0.5">{t("mp_b1_h")}</div>
            <div className="text-neutral-700">{t("mp_b1_p")}</div>
          </div>
        </div>
        <div className="nb-border rounded-md p-3 bg-[#E8F5E9] flex items-start gap-2">
          <MousePointerClick size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-xs font-medium leading-snug">
            <div className="font-bold mb-0.5">{t("mp_b2_h")}</div>
            <div className="text-neutral-700">{t("mp_b2_p")}</div>
          </div>
        </div>
        <div className="nb-border rounded-md p-3 bg-[#FFEBE8] flex items-start gap-2">
          <PartyPopper size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-xs font-medium leading-snug">
            <div className="font-bold mb-0.5">{t("mp_b3_h")}</div>
            <div className="text-neutral-700">{t("mp_b3_p")}</div>
          </div>
        </div>
      </div>

      {/* Real-app screenshots are tucked behind a modal trigger so the page
          stays clean. Click the button to view the 3 gameplay screens
          (player / share / host) in a larger zoomable view. */}
      <div className="mt-8 pt-6 border-t-2 border-dashed border-neutral-300 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-neutral-700 font-medium leading-snug max-w-md">
          {t("mp_real_blurb")}
        </div>
        <button
          type="button"
          onClick={() => setOpenScreens(true)}
          className="nb-button bg-black text-white px-5 py-2.5 rounded-md font-bold text-sm inline-flex items-center gap-2"
          data-testid="mp-open-screens"
        >
          <Eye size={16} /> {t("mp_real_open_cta")}
        </button>
      </div>

      <ScreenshotsModal open={openScreens} onClose={() => setOpenScreens(false)} t={t} />
    </section>
  );
}

// Modal that reveals the 3 real gameplay screenshots in a larger view.
// Behind a button to keep the page uncluttered (customer feedback).
function ScreenshotsModal({ open, onClose, t }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  if (!open) return null;
  const slides = [
    { slug: "player-view", titleKey: "mp_real_player_h", subKey: "mp_real_player_p" },
    { slug: "share-modal", titleKey: "mp_real_share_h", subKey: "mp_real_share_p" },
    { slug: "host-view", titleKey: "mp_real_host_h", subKey: "mp_real_host_p" },
  ];
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="mp-screens-modal"
    >
      <div
        className="bg-white nb-border rounded-lg max-w-5xl w-full my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <div className="font-display font-black text-lg tracking-tight">
            {t("mp_real_screens")}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 nb-border rounded bg-white hover:bg-neutral-100 flex items-center justify-center"
            aria-label="Close"
            data-testid="mp-screens-modal-close"
          >
            <XIcon size={18} />
          </button>
        </div>
        {/* Screenshots grid */}
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {slides.map(({ slug, titleKey, subKey }) => (
            <figure
              key={slug}
              className="nb-border rounded-md overflow-hidden bg-white flex flex-col"
              data-testid={`mp-screenshot-${slug}`}
            >
              <img
                src={`/screenshots/${slug}.png`}
                alt={t(titleKey)}
                loading="lazy"
                decoding="async"
                width="640"
                height="1138"
                className="w-full h-auto bg-neutral-50 object-contain border-b-2 border-black"
                style={{ maxHeight: "60vh" }}
              />
              <figcaption className="p-3">
                <div className="font-display font-black text-sm tracking-tight mb-0.5">{t(titleKey)}</div>
                <div className="text-xs text-neutral-700 font-medium leading-snug">{t(subKey)}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}
